/* eslint-disable no-mixed-operators */
import React, { Component } from "react";
import ReactGA from "react-ga";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { PMREMGenerator } from "three/src/extras/PMREMGenerator";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import { SkeletonUtils } from "three/examples/jsm/utils/SkeletonUtils";
import { Viewer as PanoViewer } from "photo-sphere-viewer";
import "photo-sphere-viewer/dist/photo-sphere-viewer.css";
import classnames from "classnames";
import FullWindowCanvasFactory from "./FullWindowCanvasFactory";
import {
  APP_MODE, GA_ACTION, GA_CATEGORY, APP_STAGE
} from "./constants";
import panoJpg from "./assets/pano4096.jpg";
import carPng from "./assets/car.png";
import wheelPng from "./assets/wheel.png";
import tapPng from "./assets/tap.png";
import checkPng from "./assets/check-mark.png";
import "./App.scss";

if (process.env.REACT_APP_STAGE === APP_STAGE.PRODUCTION) {
  ReactGA.initialize("UA-181515948-1");
}

const colorDetails = [
  {
    // PortofinoGrey
    buttonColor       : "#4C4C4C",
    color             : "rgb(20, 23, 26)",
    roughness         : 0.56,
    metalness         : 0.70,
    emissive          : "rgb(14, 14, 14)",
    emissiveIntensity : -0.48,
  },
  {
    // ScarletRed
    buttonColor       : "#ED0D0C",
    color             : "rgb(138, 6, 1)",
    roughness         : 0.38,
    metalness         : 0.56,
    emissive          : "rgb(8 8, 8)",
    emissiveIntensity : -1.16,
  },
  {
    // BlackNoir
    buttonColor       : "#161616",
    color             : "rgb(0, 0, 0)",
    roughness         : 0.38,
    metalness         : 0.74,
    emissive          : "rgb(23, 23, 23)",
    emissiveIntensity : 0,
  },
  {
    // LavaOrange
    buttonColor       : "#E4391B",
    color             : "rgb(148,31,18)",
    roughness         : 0.38,
    metalness         : 0.74,
    emissive          : "rgb(8, 8, 8)",
    emissiveIntensity : -1.14,
  },
  {
    // FluidMetal
    buttonColor       : "#6F6F6F",
    color             : "rgb(85, 86, 88)",
    roughness         : 0.38,
    metalness         : 0.82,
    emissive          : "rgb(23, 23, 23)",
    emissiveIntensity : 0.16,
  },
  {
    // ElectricShadow
    buttonColor       : "#434C56",
    color             : "rgb(24, 28, 37)",
    roughness         : 0.38,
    metalness         : 0.72,
    emissive          : "rgb(8, 8, 8)",
    emissiveIntensity : -0.30,
  },
  {
    // IntesneBlue
    buttonColor       : "#132258",
    color             : "rgb(6, 11, 82)",
    roughness         : 0.38,
    metalness         : 0.72,
    emissive          : "rgb(0, 0, 0)",
    emissiveIntensity : 0.24,
  },
  {
    // QuartzWhite
    buttonColor       : "#DEE1E6",
    color             : "rgb(237, 237, 237)",
    roughness         : 0.38,
    metalness         : 0.78,
    emissive          : "rgb(66, 66, 67)",
    emissiveIntensity : -0.16,
  },
];
const colorOptions = colorDetails.map(one => one.buttonColor);
const envMapIntensity = 3;

const raycaster = new window.THREE.Raycaster();
const touch = {
  isFresh: true
};
const arMode = {
  planeSize        : 300,
  cameraSize       : 3,
  shadowCameraSize : 7,
  shadowRadius     : 4,
  endScale         : 1,
  light            : {
    color     : 0xffffff,
    intensity : 0.5,
  },
  shadow: {
    color   : 0x000000,
    opacity : 0.4
  }
};
const objectMode = {
  planeSize        : 20,
  cameraSize       : 3,
  shadowCameraSize : 3,
  shadowRadius     : 4,
  endScale         : 0.7,
  light            : {
    color     : 0xffffff,
    intensity : 0.5,
  },
  shadow: {
    color   : 0x000000,
    opacity : 0.4
  }
};
const panoMode = {
  portraitZoomLvl  : 10,
  landscapeZoomLvl : 45,
};

const lights = {};
const lightDetails = {
  left: {
    color     : 0xffffff,
    x         : 3.05,
    y         : 3.67,
    z         : -9.45,
    intensity : 0.1,
  },
  right: {
    color     : 0xffffff,
    x         : 6.15,
    y         : 5.75,
    z         : 16.6,
    intensity : 0.1,
  },
  front: {
    color     : 0xffffff,
    x         : -7.75,
    y         : 4.56,
    z         : 0.5,
    intensity : 0.1,
  },
  back: {
    color     : 0xffffff,
    x         : 11.8,
    y         : 2.5,
    z         : 0.6,
    intensity : 0.1,
  },
};

function getAngle(v1, v2) {
  const angle = Math.acos(
    (v1.x * v2.x + v1.y * v2.y) / (v1.length() * v2.length())
  );
  if (v1.x * v2.y - v1.y * v2.x >= 0) {
    return -angle;
  }
  return angle;
}

function setupRenderer(renderer) {
  renderer.outputEncoding = window.THREE.sRGBEncoding;
  renderer.toneMapping = window.THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.85;

  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = window.THREE.PCFShadowMap;

  return renderer;
}

function setupEnvMap(scene, renderer) {
  new RGBELoader()
    .setDataType(window.THREE.UnsignedByteType)
    .setPath("HDR/")
    .load("envMap.hdr", (texture) => {
      const pmremGenerator = new PMREMGenerator(renderer);
      pmremGenerator.compileEquirectangularShader();

      const envMap = pmremGenerator.fromEquirectangular(texture).texture;
      scene.environment = envMap;

      pmremGenerator.dispose();
    });
}

function updateZoomLvl(panoViewer, orientation) {
  if (orientation === 0 || orientation === 180) {
    panoViewer.zoom(panoMode.portraitZoomLvl);
  } else {
    panoViewer.zoom(panoMode.landscapeZoomLvl);
  }
}

class View extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modelFileReady : false,
      placed         : false,
      appMode        : APP_MODE.VEHICLE,
      selectedColor  : colorOptions[0]
    };
  }

  componentDidMount() {
    // Init PanoViewer
    panoMode.viewer = new PanoViewer({
      container      : this.panoContainer,
      panorama       : panoJpg,
      minFov         : 30,
      maxFov         : 110,
      defaultZoomLvl : 10,
      defaultLong    : 0,
      defaultLat     : -0.4,
      navbar         : null
    });

    // Start AR
    const load = () => {
      window.XRExtras.Loading.showLoading({ onxrloaded: this.onxrloaded });
    };
    if (window.XRExtras) {
      load();
    } else {
      window.addEventListener("xrextrasloaded", load);
    }

    // GLTF loader
    const loader = new GLTFLoader().setPath("Exterior/");
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("/libs/draco/");
    loader.setDRACOLoader(dracoLoader);

    // Load GLTF object
    loader.load(
      "model.glb",
      (gltf) => {
        // Load model file
        gltf.scene.traverse((child) => {
          if (child.isMesh) {
            // Shadow
            child.castShadow    = true;
            child.receiveShadow = true;

            // Env map intensity
            child.material.envMapIntensity = envMapIntensity;

            // Car surface
            if (child.material.name === "ext_carpaint_SHD") {
              child.userData.switchColor = true;
            }

            // Shadow
            if (child.userData.name === "x_studio_bake_v01-x_studio_bake_floor") {
              // Disable shadows cast/receive on this plane
              child.castShadow    = false;
              child.receiveShadow = false;
              child.frustumCulled = false;

              // Use a basic material and disable depth writes for transparent floor shadow
              child.material = new window.THREE.MeshBasicMaterial({
                blending    : window.THREE.MultiplyBlending,
                toneMapped  : false,
                map         : child.material.map,
                opacity     : 1,
                depthTest   : true,
                depthWrite  : false,
                transparent : true,
              });
            }

            // Windows
            if (child.material.name === "e_windows_tint") {
              child.material = new window.THREE.MeshPhysicalMaterial({
                color           : 0xffffff,
                metalness       : 0,
                roughness       : 0.1,
                transmission    : 0.9,
                transparent     : true,
                envMapIntensity : 1
              });
              child.userData.windows = true;
            }

            // Wheel tyres
            if (child.material.name === "w_tyres_LTD_SHD") {
              child.material.envMapIntensity = 0.1;
              child.userData.wheelTyre = true;
            }
          }
        });
        // Add lights
        Object.keys(lightDetails).forEach((direction) => {
          const { color, x, y, z, intensity } = lightDetails[direction];

          const dirLight = new window.THREE.DirectionalLight(color, intensity);
          dirLight.position.set(x, y, z);
          dirLight.castShadow = true;

          gltf.scene.add(dirLight);
          lights[direction] = dirLight;
        });

        // Set model files
        arMode.modelFile = gltf;
        objectMode.modelFile = { scene: SkeletonUtils.clone(gltf.scene) };
        objectMode.modelFile.scene.traverse((child) => {
          if (child.isMesh) {
            child.material = child.material.clone();
          }
        });
        this.setState({ modelFileReady: true });

        // Set initial color
        this.onClickColor(colorOptions[0]);

        // Init object mode
        this.initObjectPreview();
      },
      (xhr) => { console.log(`GLTF object ${xhr.loaded}/${xhr.total} loaded`); },
      (error) => { console.log("An error happened", error); }
    );

    // Log GA event
    if (process.env.REACT_APP_STAGE === APP_STAGE.PRODUCTION) {
      ReactGA.event({
        category : GA_CATEGORY,
        action   : GA_ACTION.LAUNCH
      });
    }
  }

  initObjectPreview() {
    // Init renderer
    objectMode.renderer = new window.THREE.WebGLRenderer({
      canvas    : this.objectCanvas,
      antialias : true
    });
    objectMode.renderer.setPixelRatio(window.devicePixelRatio);
    objectMode.renderer.setSize(window.innerWidth, window.innerHeight);
    setupRenderer(objectMode.renderer);

    // Init scene
    objectMode.scene = new window.THREE.Scene();
    objectMode.scene.background = new window.THREE.Color(0xeeeeee);

    // Init camera
    const camera = new window.THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(
      objectMode.cameraSize, objectMode.cameraSize, objectMode.cameraSize
    );

    // Add model
    objectMode.modelFile.scene.position.set(0, 0, 0);
    objectMode.modelFile.scene.rotation.set(0, 1.9, 0);
    objectMode.modelFile.scene.scale.setScalar(objectMode.endScale);
    objectMode.scene.add(objectMode.modelFile.scene);

    // Add envMap
    setupEnvMap(objectMode.scene, objectMode.renderer);

    // OrbitControls
    const controls = new OrbitControls(camera, this.objectEventEl);
    controls.update();

    // Run renderer
    const animate = () => {
      requestAnimationFrame(animate);

      if (this.state.appMode === APP_MODE.OBJECT) {
        objectMode.renderer.render(objectMode.scene, camera);
      }
    };
    animate();

    // On resize
    window.addEventListener("resize", () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();

      objectMode.renderer.setSize(window.innerWidth, window.innerHeight);
    }, false);
  }

  placegroundScenePipelineModule = () => {
    const initXrScene = ({ scene, camera, renderer }) => {
      // Envmap lighting
      setupEnvMap(scene, renderer);

      // Add surface to receive shadow
      arMode.surface = new window.THREE.Mesh(
        new window.THREE.PlaneGeometry(arMode.planeSize, arMode.planeSize),
        new window.THREE.MeshBasicMaterial({
          color       : 0xffff00,
          transparent : true,
          opacity     : 0.0,
          depthTest   : true,
          depthWrite  : true,
          side        : window.THREE.DoubleSide
        })
      );
      arMode.surface.rotateX(-Math.PI / 2);
      arMode.surface.position.set(0, -0.1, 0);
      arMode.surface.geometry.computeBoundingBox();
      arMode.surface.geometry.computeVertexNormals();
      arMode.surface.frustumCulled = false;
      scene.add(arMode.surface);

      // Set the initial camera position relative to the scene we just laid out. This must be at a height greater than y=0.
      camera.position.set(0, arMode.cameraSize, 0);
    };

    return {
      // Pipeline modules need a name. It can be whatever you want but must be unique within your app.
      name: "placeground",

      // onStart is called once when the camera feed begins. In this case, we need to wait for the
      // XR8.Threejs scene to be ready before we can access it to add content. It was created in
      // XR8.Threejs.pipelineModule()'s onStart method.
      onStart: ({ orientation }) => {
        const { scene, camera, renderer } = window.XR8.Threejs.xrScene();  // Get the 3js sceen from xr3js.

        setupRenderer(renderer);
        initXrScene({ scene, camera, renderer }); // Add objects to the scene and set starting camera position.

        // Add touch event listeners
        this.arEventEl.addEventListener("touchstart", this.onTouchStart, true);
        this.arEventEl.addEventListener("touchend", this.onTouchEnd, true);
        this.arEventEl.addEventListener("touchmove", this.onTouchMove, true);

        // Sync the xr controller's 6DoF position and camera paremeters with our scene.
        window.XR8.XrController.updateCameraProjectionMatrix({
          origin : camera.position,
          facing : camera.quaternion,
        });

        // Clear QRCode interval
        clearInterval(window.qrcodeInterval);

        // Update Pano Zoom Lvl
        updateZoomLvl(panoMode.viewer, orientation);
      },
      onDeviceOrientationChange: ({ orientation }) => {
        updateZoomLvl(panoMode.viewer, orientation);
      },
    };
  }

  onxrloaded = () => {
    // Update QRCode color
    window.qrcodeInterval = setInterval(() => {
      const stops = document.getElementsByTagName("stop");
      if (stops.length === 4) {
        for (const oneStop of stops)  {
          oneStop.setAttribute("stop-color", "#0ba3d8");
        }
        document.getElementById("qrcode").style.visibility = "visible";
      }
    }, 200);

    window.XR8.addCameraPipelineModules([  // Add camera pipeline modules.
      // Existing pipeline modules.
      window.XR8.GlTextureRenderer.pipelineModule(),      // Draws the camera feed.
      window.XR8.Threejs.pipelineModule(),                // Creates a ThreeJS AR Scene.
      window.XR8.XrController.pipelineModule(),           // Enables SLAM tracking.
      window.XRExtras.AlmostThere.pipelineModule(),       // Detects unsupported browsers and gives hints.
      // window.XRExtras.FullWindowCanvas.pipelineModule(),  // Modifies the canvas to fill the window.
      FullWindowCanvasFactory().pipelineModule(),
      window.XRExtras.Loading.pipelineModule(),           // Manages the loading screen on startup.
      window.XRExtras.RuntimeError.pipelineModule(),      // Shows an error image on runtime error.
      // Custom pipeline modules.
      this.placegroundScenePipelineModule(),
    ]);

    // Open the camera and start running the camera run loop.
    window.XR8.run({ canvas: this.arCanvas });
  }

  onTouchStart = (e) => {
    if (e.touches.length === 1) {
      touch.oneTouch = e.touches[0];
    } else {
      touch.oneTouch = null;
    }
  }

  onTouchEnd = (e) => {
    if (e.touches.length === 0) {
      touch.isFresh = true;
    }

    if (!touch.oneTouch) return;

    const { placed } = this.state;
    if (placed) return;
    if (!arMode.modelFile) return;

    this.placeObject();
  }

  onTouchMove = (e) => {
    const { placed } = this.state;
    touch.oneTouch = null;
    e.preventDefault();

    // Get touch[0] position
    const { camera } = window.XR8.Threejs.xrScene();
    const tapPosition = new window.THREE.Vector2(
      (e.touches[0].clientX / window.innerWidth) * 2 - 1,
      -(e.touches[0].clientY / window.innerHeight) * 2 + 1
    );
    raycaster.setFromCamera(tapPosition, camera);

    if (touch.isFresh) {
      touch.isFresh        = false;
      touch.isDragging     = false;
      touch.originalVector = null;

      if (!placed) return;

      if (e.touches.length === 2) {
        touch.originalVector = new window.THREE.Vector2(
          e.touches[1].clientX - e.touches[0].clientX,
          e.touches[1].clientY - e.touches[0].clientY
        );
        touch.originalScale    = arMode.modelFile.scene.scale.x;
        touch.originalRotation = arMode.modelFile.scene.rotation.y;
      } else if (e.touches.length === 1) {
        const pointing = raycaster.intersectObject(arMode.modelFile.scene, true).length > 0;

        if (pointing) {
          touch.isDragging = true;

          // Get object's screen position
          const vector = arMode.modelFile.scene.position.clone();
          vector.project(camera);
          vector.x = Math.round((vector.x + 1) * window.innerWidth / 2);
          vector.y = Math.round((-vector.y + 1) * window.innerHeight / 2);

          // Save offset between user's touch point and object's position
          touch.dragOffset = new window.THREE.Vector2(
            e.touches[0].clientX - vector.x,
            e.touches[0].clientY - vector.y
          );
        }
      }

      return;
    }

    if (touch.isDragging) {
      // Update raycaster considering offset
      raycaster.setFromCamera(new window.THREE.Vector2(
        +((e.touches[0].clientX - touch.dragOffset.x) / window.innerWidth)  * 2 - 1,
        -((e.touches[0].clientY - touch.dragOffset.y) / window.innerHeight) * 2 + 1
      ), camera);

      const intersects = raycaster.intersectObject(arMode.surface);
      const surfaceIntersect = intersects.find(i => i.object === arMode.surface);
      if (surfaceIntersect) {
        arMode.modelFile.scene.position.set(
          surfaceIntersect.point.x,
          0.0,
          surfaceIntersect.point.z
        );
      }
      return;
    }

    if (e.touches.length !== 2) return;

    if (touch.originalVector) {
      const newVector = new window.THREE.Vector2(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY
      );

      // pinch
      const newScale = touch.originalScale / touch.originalVector.length() * newVector.length();
      arMode.modelFile.scene.scale.setScalar(newScale);

      // rotate
      const angle = getAngle(touch.originalVector, newVector);
      arMode.modelFile.scene.rotation.set(
        0, touch.originalRotation + angle, 0
      );
    }
  }

  placeObject = () => {
    const xrScene = window.XR8.Threejs.xrScene();

    const tapPosition = new window.THREE.Vector2(0, 0);
    raycaster.setFromCamera(tapPosition, xrScene.camera);

    // Raycast against the "surface" object.
    const intersects = raycaster.intersectObject(arMode.surface);
    const surfaceIntersect = intersects.find(i => i.object === arMode.surface);
    if (!surfaceIntersect) return;

    // Place the object
    arMode.modelFile.scene.rotation.set(0, 0.9, 0);
    arMode.modelFile.scene.position.set(surfaceIntersect.point.x, 0.0, surfaceIntersect.point.z);

    // Set size
    const endScale = 1;
    arMode.modelFile.scene.scale.setScalar(endScale);

    // Add model to the screen
    xrScene.scene.add(arMode.modelFile.scene);

    // Update state
    this.setState({ placed: true });

    // Log GA event
    if (process.env.REACT_APP_STAGE === APP_STAGE.PRODUCTION) {
      ReactGA.event({
        category : GA_CATEGORY,
        action   : GA_ACTION.PLACE_OBJECT
      });
    }
  };

  onClickColor = (colorOption) => {
    this.setState({ selectedColor: colorOption });

    const colorDetail = colorDetails.find(one => one.buttonColor === colorOption);
    const setModelColor = (modelFile) => {
      modelFile.scene.traverse((child) => {
        if (child.isMesh && child.userData.switchColor) {
          child.material = new window.THREE.MeshPhysicalMaterial({
            color              : colorDetail.color,
            metalness          : colorDetail.metalness,
            roughness          : colorDetail.roughness,
            emissive           : colorDetail.emissive,
            emissiveIntensity  : colorDetail.emissiveIntensity,
            clearcoat          : 0.05,
            clearcoatRoughness : 0.05,
            envMapIntensity
          });
        }
      });
    };
    if (arMode.modelFile) {
      setModelColor(arMode.modelFile);
    }
    if (objectMode.modelFile) {
      setModelColor(objectMode.modelFile);
    }
  }

  startInterior = () => {
    // const { appMode } = this.state;
    // if (appMode === APP_MODE.VEHICLE) {
    //   window.XR8.pause();
    // }
    this.setState({ appMode: APP_MODE.INTERIOR });

    // Log GA event
    if (process.env.REACT_APP_STAGE === APP_STAGE.PRODUCTION) {
      ReactGA.event({
        category : GA_CATEGORY,
        action   : GA_ACTION.PANO_VIEW
      });
    }
  }

  startVehicle = () => {
    // const { appMode } = this.state;
    // if (appMode !== APP_MODE.VEHICLE) {
    //   window.XR8.resume();
    // }
    this.setState({ appMode: APP_MODE.VEHICLE });
  }

  startObject = () => {
    // const { appMode } = this.state;
    // if (appMode === APP_MODE.VEHICLE) {
    //   window.XR8.pause();
    // }
    this.setState({ appMode: APP_MODE.OBJECT });

    // Log GA event
    if (process.env.REACT_APP_STAGE === APP_STAGE.PRODUCTION) {
      ReactGA.event({
        category : GA_CATEGORY,
        action   : GA_ACTION.OBJECT_MODE
      });
    }
  }

  render() {
    const {
      modelFileReady, placed, appMode, selectedColor
    } = this.state;

    return (
      <>
        <canvas
          ref={(el) => { this.arCanvas = el; }}
          style={{ visibility: appMode === APP_MODE.VEHICLE ? "visible" : "hidden" }}
        />
        <canvas
          ref={(el) => { this.objectCanvas = el; }}
          style={{ visibility: appMode === APP_MODE.OBJECT ? "visible" : "hidden" }}
        />
        <div>
          { !modelFileReady && (
            <div className="loading-overlay-div">
              Loading GLTF...
            </div>
          )}
          <div
            className="ar-container"
            style={{ visibility: appMode === APP_MODE.VEHICLE ? "visible" : "hidden" }}
            ref={(el) => { this.arEventEl = el; }}
          >
            { modelFileReady && !placed && (
              <div className="tap-div">
                <img src={tapPng} alt="tap" />
                <p>
                  Tap to place
                </p>
              </div>
            )}
          </div>
          <div
            className="object-container"
            style={{ visibility: appMode === APP_MODE.OBJECT ? "visible" : "hidden" }}
            ref={(el) => { this.objectEventEl = el; }}
          />
          <div
            className="color-container"
            style={{ visibility: (
              appMode === APP_MODE.VEHICLE && placed
              || appMode === APP_MODE.OBJECT
            ) ? "visible" : "hidden" }}
          >
            { colorOptions.map(colorOption => (
              <div
                key={colorOption}
                className={classnames({
                  "color-option" : true,
                  active         : selectedColor === colorOption
                })}
                onClick={() => this.onClickColor(colorOption)}
              >
                <div style={{ backgroundColor: colorOption }}>
                  { selectedColor === colorOption && (
                    <img src={checkPng} alt="check" />
                  )}
                </div>
              </div>
            ))}
          </div>
          <div
            className="pano-container"
            style={{ visibility: appMode === APP_MODE.INTERIOR ? "visible" : "hidden" }}
            ref={(el) => { this.panoContainer = el; }}
          />
          <div
            className="switch-container"
            style={{ visibility: appMode !== APP_MODE.INTERIOR ? "visible" : "hidden", }}
          >
            <div
              className={classnames({
                "button-div" : true,
                active       : appMode === APP_MODE.VEHICLE
              })}
              onClick={this.startVehicle}
            >
              AR
            </div>
            <div
              className={classnames({
                "button-div" : true,
                active       : appMode === APP_MODE.OBJECT
              })}
              onClick={this.startObject}
            >
              Object
            </div>
          </div>
          <div className="button-container">
            <div
              className={classnames({
                "button-div" : true,
                vehicle      : true,
                active       : appMode === APP_MODE.VEHICLE
              })}
              onClick={this.startVehicle}
            >
              <img src={carPng} alt="car" />
            </div>
            <div
              className={classnames({
                "button-div" : true,
                interior     : true,
                active       : appMode === APP_MODE.INTERIOR
              })}
              onClick={this.startInterior}
            >
              <img src={wheelPng} alt="wheel" />
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default View;
